// Delay, drawn: the two sides (synced to the beat or free in ms), Link and
// Ping Pong, the feedback, the band filter in the loop, Freeze and Dry/Wet —
// and the echoes they make, as taps fading by the feedback, so a turned
// knob shows what it does before it's heard.

import { useMemo } from "react";
import { clamp, DELAY_SIXTEENTHS, delayModel, fromNorm, readOn, writeHz, writeMs, type DelaySide } from "@web-shared/lib/device-params";
import { fmtHz, fmtMs, fmtPct, Knob, StepSelect, Toggle } from "@web-shared/components/devices/controls";
import type { EditorProps } from "@web-shared/components/devices/types";

const W = 220;
const H = 56;

export function DelayEditor({ device, bpm, disabled, set }: EditorProps) {
  const m = useMemo(() => delayModel(device, bpm), [device, bpm]);
  const param = (i: number | undefined) => (i === undefined ? undefined : device.params[i]);
  const toggle = (i: number | undefined, on: boolean) => {
    const p = param(i);
    if (p && i !== undefined) set(i, on ? p.max : p.min, true);
  };

  const taps = useMemo(() => {
    const out: Array<{ t: number; level: number; side: "L" | "R" }> = [];
    const horizon = Math.max(m.left.timeSec, m.right.timeSec) * 6;
    if (m.pingPong) {
      let t = 0;
      let level = 1;
      for (let i = 0; i < 12 && level > 0.03; i++) {
        t += i % 2 === 0 ? m.left.timeSec : m.right.timeSec;
        out.push({ t, level, side: i % 2 === 0 ? "L" : "R" });
        level *= m.freeze ? 1 : m.feedback;
      }
    } else {
      for (const side of ["L", "R"] as const) {
        const dt = side === "L" ? m.left.timeSec : m.right.timeSec;
        let level = 1;
        for (let i = 1; i <= 8 && level > 0.03 && dt * i <= horizon; i++) {
          out.push({ t: dt * i, level, side });
          level *= m.freeze ? 1 : m.feedback;
        }
      }
    }
    return { taps: out, horizon: Math.max(0.05, ...out.map((x) => x.t)) * 1.08 };
  }, [m]);

  const sideControls = (label: "L" | "R", side: DelaySide) => {
    const sync = param(side.idx.sync);
    const div = param(side.idx.division);
    const time = param(side.idx.time);
    const linkedR = label === "R" && m.link;
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold text-muted-foreground">{label}</span>
          {sync && <Toggle label={side.sync ? "Sync" : "Time"} on={side.sync} disabled={disabled || linkedR} onChange={(on) => toggle(side.idx.sync, on)} title="Sync to the beat, or a free time in ms" />}
        </div>
        {side.sync && div ? (
          div.items && div.items.length > 0 ? (
            <div className="w-[60px]">
              <StepSelect
                label="16th"
                value={Math.round(div.value - div.min)}
                options={div.items.map((s, i) => ({ value: i, label: s }))}
                disabled={disabled || linkedR}
                onChange={(i) => set(side.idx.division!, div.min + i, true)}
              />
            </div>
          ) : (
            <Knob
              label="16th"
              value={div.value}
              min={div.min}
              max={div.max}
              step={1}
              format={(v) => String(DELAY_SIXTEENTHS[clamp(Math.round(v - div.min), 0, DELAY_SIXTEENTHS.length - 1)] ?? Math.round(v))}
              disabled={disabled || linkedR}
              onDraft={(v) => set(side.idx.division!, v, false)}
              onCommit={(v) => set(side.idx.division!, v, true)}
            />
          )
        ) : time ? (
          <Knob
            label="Time"
            value={side.timeSec * 1000}
            min={1}
            max={5000}
            log
            format={fmtMs}
            disabled={disabled || linkedR}
            onDraft={(v) => set(side.idx.time!, writeMs(time, v, 1, 5000), false)}
            onCommit={(v) => set(side.idx.time!, writeMs(time, v, 1, 5000), true)}
          />
        ) : null}
        <span className="tabular text-[9px] text-muted-foreground">{fmtMs(side.timeSec * 1000)}</span>
      </div>
    );
  };

  const fbP = param(m.idx.feedback);
  const mixP = param(m.idx.mix);
  const fFreq = param(m.idx.filterFreq);
  const fWidth = param(m.idx.filterWidth);

  return (
    <div className="flex flex-col gap-2" data-testid="delay">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[56px] w-[220px] rounded bg-[var(--bg-deep)]" role="img" aria-label="Delay echoes">
        <line x1={0} x2={W} y1={H / 2} y2={H / 2} stroke="var(--border-panel)" />
        <rect x={1} y={4} width={3} height={H / 2 - 4} fill="var(--foreground)" opacity={0.6} />
        {taps.taps.map((t, i) => {
          const x = (t.t / taps.horizon) * W;
          const h = (H / 2 - 6) * t.level;
          return <rect key={i} x={x - 1.5} y={t.side === "L" ? H / 2 - h : H / 2} width={3} height={h} fill={t.side === "L" ? "var(--primary)" : "var(--solo)"} opacity={0.4 + 0.6 * t.level} />;
        })}
        <text x={W - 3} y={10} fontSize={8} textAnchor="end" fill="var(--muted-foreground)">
          L
        </text>
        <text x={W - 3} y={H - 3} fontSize={8} textAnchor="end" fill="var(--muted-foreground)">
          R
        </text>
      </svg>
      <div className="flex items-start gap-2">
        {sideControls("L", m.left)}
        <div className="flex flex-col items-center gap-1 pt-1">
          {m.idx.link !== undefined && <Toggle label="Link" on={m.link} disabled={disabled} onChange={(on) => toggle(m.idx.link, on)} title="The right side follows the left" />}
          {m.idx.pingPong !== undefined && <Toggle label="Ping Pong" on={m.pingPong} disabled={disabled} onChange={(on) => toggle(m.idx.pingPong, on)} title="Echoes bounce between left and right" />}
          {m.idx.freeze !== undefined && <Toggle label="Freeze" on={m.freeze} disabled={disabled} onChange={(on) => toggle(m.idx.freeze, on)} title="Hold what's in the line, take no more input" />}
        </div>
        {sideControls("R", m.right)}
      </div>
      <div className="flex items-end gap-1">
        {fbP && (
          <Knob label="Feedback" value={clamp((fbP.value - fbP.min) / (fbP.max - fbP.min || 1), 0, 1)} min={0} max={1} format={fmtPct} disabled={disabled} onDraft={(t) => set(m.idx.feedback!, fromNorm(fbP, t), false)} onCommit={(t) => set(m.idx.feedback!, fromNorm(fbP, t), true)} />
        )}
        {m.idx.filterOn !== undefined && (
          <div className="mb-4">
            <Toggle label="Filter" on={m.filterOn} disabled={disabled} onChange={(on) => toggle(m.idx.filterOn, on)} title="A band-pass filter in the feedback loop" />
          </div>
        )}
        {fFreq && (
          <Knob label="Freq" value={m.filterFreq} min={50} max={18000} log format={fmtHz} disabled={disabled || !m.filterOn} onDraft={(v) => set(m.idx.filterFreq!, writeHz(fFreq, v, 50, 18000), false)} onCommit={(v) => set(m.idx.filterFreq!, writeHz(fFreq, v, 50, 18000), true)} />
        )}
        {fWidth && (
          <Knob
            label="Width"
            value={fWidth.value}
            min={fWidth.min}
            max={fWidth.max}
            format={(v) => v.toFixed(1)}
            disabled={disabled || !m.filterOn}
            onDraft={(v) => set(m.idx.filterWidth!, v, false)}
            onCommit={(v) => set(m.idx.filterWidth!, v, true)}
          />
        )}
        {mixP && (
          <Knob label="Dry/Wet" value={clamp((mixP.value - mixP.min) / (mixP.max - mixP.min || 1), 0, 1)} min={0} max={1} format={fmtPct} disabled={disabled} onDraft={(t) => set(m.idx.mix!, fromNorm(mixP, t), false)} onCommit={(t) => set(m.idx.mix!, fromNorm(mixP, t), true)} />
        )}
      </div>
      {readOn(param(m.idx.freeze), false) && <div className="text-[9px] text-[var(--changed)]">Frozen — the line holds what it has.</div>}
    </div>
  );
}
